import { IdleAnimation, IdleAnimationStage, delay, waitForAnimationEnd } from './idle-animation';
import './mario-level-animation.css';

// --- silhouettes ----------------------------------------------------------
// Flat, single-colour (fill: currentColor) shadow puppets in the same
// hand-authored style as the toy parade. Shapes are original approximations
// evoking a classic side-scrolling platformer without reproducing any
// copyrighted artwork: a dungaree-clad, capped running plumber; a scowling
// mushroom-footed walker; warp pipes; brick and question blocks; rolling hills
// and puffy clouds. Everything reads as a silhouette so the specific likeness
// stays generic.

// The running hero: a capped, big-nosed figure mid-stride with dungarees and
// stubby boots. Authored facing right (the direction of travel). The run cycle
// is a true two-frame leg swap: a shared static upper body (head, cap, torso,
// arms) plus two overlaid leg frames that a CSS steps() keyframe alternates —
// no skew/jitter, just clean frame cycling like a sprite.

// Shared upper body: cap, head, nose, torso/dungarees and both arms. Legs are
// intentionally omitted here; the two leg frames below supply them.
const HERO_BODY_SVG = `
<svg viewBox="0 0 30 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 5 C8 2 12 1 16 1.4 C21 2 23 4 23 6.2 L23.4 8.4 L7.6 8.4 Z"/>
  <rect x="6.2" y="7.4" width="18" height="2.6" rx="1"/>
  <path d="M22.6 8.6 L27 9.4 L27 11.6 L22.6 11.2 Z"/>
  <circle cx="12.4" cy="12.4" r="4.2"/>
  <rect x="16.4" y="10.6" width="3.2" height="3" rx="1"/>
  <path d="M9 16.5 H21 L20 27 H10 Z"/>
  <path d="M8 17 L4 22 L6 24 L11 19.5 Z"/>
  <path d="M22 17 L26.5 20.5 L25 22.8 L20 19.6 Z"/>
</svg>`;

// Run frame A: front (right) leg reaching forward, rear (left) leg trailing back
// — the classic sprint "reach" pose.
const HERO_LEGS_A_SVG = `
<svg viewBox="0 0 30 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M15 26.5 L20 33 L23.4 31 L18.5 25.5 Z"/>
  <rect x="20.2" y="31.4" width="6.6" height="3.4" rx="1.2" transform="rotate(20 23.5 33.1)"/>
  <path d="M13 26.5 L9 33.5 L12 35 L15 27.5 Z"/>
  <rect x="6.8" y="34.4" width="6.4" height="3.4" rx="1.2"/>
</svg>`;

// Run frame B: legs crossed under the body (the "pass" pose) — front leg driving
// down, rear leg lifting to swing through.
const HERO_LEGS_B_SVG = `
<svg viewBox="0 0 30 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M15.5 26.5 L16.5 35.5 L20 35.5 L18.5 26 Z"/>
  <rect x="15.4" y="34.6" width="6.4" height="3.4" rx="1.2"/>
  <path d="M12.5 26.5 L11.5 30.5 L15 31.5 L15.5 27 Z"/>
  <rect x="8.6" y="29.4" width="6.4" height="3.2" rx="1.2" transform="rotate(-14 11.8 31)"/>
</svg>`;

// The Goomba-like walker: a squat, angry mushroom cap on two little feet.
const WALKER_SVG = `
<svg viewBox="0 0 30 26" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M4 16 C4 7 9 2 15 2 C21 2 26 7 26 16 L26 18 L4 18 Z"/>
  <path d="M9.5 9.5 L12.5 12.5 L9 12.5 Z" fill="#fff" opacity="0.85"/>
  <path d="M20.5 9.5 L17.5 12.5 L21 12.5 Z" fill="#fff" opacity="0.85"/>
  <rect x="5" y="18" width="8" height="6" rx="2.4"/>
  <rect x="17" y="18" width="8" height="6" rx="2.4"/>
</svg>`;

// A flattened walker for the "stomped" beat — squashed to a wide, thin cap.
const WALKER_FLAT_SVG = `
<svg viewBox="0 0 30 26" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M2 20 C2 16 8 14 15 14 C22 14 28 16 28 20 L28 23 L2 23 Z"/>
</svg>`;

// A warp pipe: a wide rim over a narrower body. preserveAspectRatio="none" lets
// it stretch to exactly fill its box (of any height) so the base always plants
// flush on the ground line — the tall variant otherwise letterboxed and floated.
const PIPE_SVG = `
<svg viewBox="0 0 34 40" preserveAspectRatio="none" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="34" height="9" rx="2"/>
  <rect x="4.5" y="9" width="25" height="31"/>
</svg>`;

// A brick block: a square with a masonry seam cross so it reads as breakable.
const BRICK_SVG = `
<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="24" height="24" rx="1.5"/>
  <rect x="0" y="11" width="24" height="2" fill="#fff" opacity="0.55"/>
  <rect x="11" y="0" width="2" height="11" fill="#fff" opacity="0.55"/>
  <rect x="5" y="13" width="2" height="11" fill="#fff" opacity="0.55"/>
  <rect x="17" y="13" width="2" height="11" fill="#fff" opacity="0.55"/>
</svg>`;

// A question block: a rounded square with a bold "?" cut out.
const QUESTION_SVG = `
<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="24" height="24" rx="3"/>
  <path d="M8 8.5 C8 5 11 4 12.4 4 C15 4 16.6 5.6 16.6 8 C16.6 10.4 13 10.8 13 13.4 L11 13.4 C11 9.8 14.4 9.6 14.4 8 C14.4 6.8 13.4 6 12.2 6 C10.6 6 10 7.2 10 8.5 Z" fill="#fff" opacity="0.75"/>
  <rect x="11" y="15.5" width="2" height="2.4" rx="0.6" fill="#fff" opacity="0.75"/>
</svg>`;

// A brick shard for the break-burst (a small chunk that flies off and falls).
const SHARD_SVG = `
<svg viewBox="0 0 10 10" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="0" y="0" width="10" height="10" rx="1.4"/>
</svg>`;

// Rolling background hill.
const HILL_SVG = `
<svg viewBox="0 0 80 40" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <path d="M0 40 C10 40 18 8 40 8 C62 8 70 40 80 40 Z"/>
</svg>`;

// Puffy cloud.
const CLOUD_SVG = `
<svg viewBox="0 0 60 26" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <circle cx="16" cy="16" r="10"/>
  <circle cx="30" cy="11" r="12"/>
  <circle cx="45" cy="16" r="10"/>
  <rect x="14" y="16" width="34" height="10" rx="4"/>
</svg>`;

// The end-of-level flagpole: a slim pole on a stepped base with a triangular
// pennant flying from the top.
const FLAG_SVG = `
<svg viewBox="0 0 20 78" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <circle cx="10" cy="4" r="3"/>
  <rect x="8.6" y="4" width="2.8" height="70"/>
  <path d="M8.6 8 L1 12.5 L8.6 17 Z"/>
  <rect x="3" y="72" width="14" height="6" rx="1.5"/>
</svg>`;

// The castle Mario finishes at: a battlemented keep with a central gateway
// (the "door" the runner stops in front of) and a small flag on the turret.
const CASTLE_SVG = `
<svg viewBox="0 0 96 66" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
  <rect x="8" y="20" width="80" height="46"/>
  <rect x="8" y="14" width="10" height="6"/>
  <rect x="26" y="14" width="10" height="6"/>
  <rect x="60" y="14" width="10" height="6"/>
  <rect x="78" y="14" width="10" height="6"/>
  <rect x="38" y="4" width="20" height="16"/>
  <rect x="38" y="0" width="4" height="4"/>
  <rect x="46" y="0" width="4" height="4"/>
  <rect x="54" y="0" width="4" height="4"/>
  <path d="M40 66 C40 52 44 46 48 46 C52 46 56 52 56 66 Z" fill="#fff" opacity="0.5"/>
  <rect x="16" y="30" width="12" height="12" rx="1" fill="#fff" opacity="0.4"/>
  <rect x="68" y="30" width="12" height="12" rx="1" fill="#fff" opacity="0.4"/>
</svg>`;
// -------------------------------------------------------------------------

/**
 * A silhouette side-scrolling platformer vignette played on the
 * `footer-below-calendar` stage (a strip on the footer line, clamped to the
 * calendar's width). A capped runner sprints across a scrolling "level":
 * it jumps a pit, head-bumps a row of brick/question blocks (bursting a couple
 * of bricks into shards), stomps a mushroom-footed walker flat, then runs off
 * the right edge.
 *
 * One entry in the {@link IdleAnimationDirector} registry. It only owns its
 * choreography: it spawns actors, lets CSS keyframes drive the scroll/run/jump,
 * scripts the discrete beats with {@link delay}, and resolves once the runner
 * finishes crossing (or the pass is aborted), removing every node it created.
 */
export class MarioLevelAnimation implements IdleAnimation {
  readonly name = 'mario-level';
  // Runs the "level" on the footer line, clamped to the calendar's width.
  readonly stage: IdleAnimationStage = 'footer-below-calendar';

  // Phase durations (ms). The sequence is: scroll in until the first brick is
  // above Mario, pause while Mario jumps and breaks it, then resume scrolling
  // the rest of the level (walker stomp + exit).
  private static readonly SCROLL_IN_MS = 5200;
  private static readonly JUMP_MS = 620;
  private static readonly SCROLL_REST_MS = 6000;

  async play(stage: HTMLElement, signal: AbortSignal): Promise<void> {
    if (signal.aborted) {
      return;
    }

    const created: HTMLElement[] = [];
    const add = (el: HTMLElement): HTMLElement => {
      created.push(el);
      return el;
    };
    const cleanup = (): void => {
      for (const el of created) {
        el.remove();
      }
      // The stage element is owned by the director and reused across passes;
      // clear the finish/fade class so the next run starts fully opaque.
      stage.classList.remove('mario-stage--finish');
    };

    try {
      // A scrolling world layer that carries all the scenery. It scrolls in two
      // phases (in, then rest) driven by JS-applied classes, pausing between so
      // Mario can jump and break the first brick while the background is frozen.
      const world = add(document.createElement('div'));
      world.className = 'mario-world';
      const totalMs =
        MarioLevelAnimation.SCROLL_IN_MS +
        MarioLevelAnimation.JUMP_MS +
        MarioLevelAnimation.SCROLL_REST_MS;
      world.style.setProperty('--mario-total-ms', `${totalMs}ms`);
      world.style.setProperty('--mario-in-ms', `${MarioLevelAnimation.SCROLL_IN_MS}ms`);
      world.style.setProperty('--mario-rest-ms', `${MarioLevelAnimation.SCROLL_REST_MS}ms`);

      // Background parallax scenery (hills + clouds) drifts slower than the
      // foreground so the level reads with a little depth.
      world.appendChild(this.actor('mario-scenery mario-scenery--cloud', CLOUD_SVG, { left: '18%' }));
      world.appendChild(this.actor('mario-scenery mario-scenery--cloud', CLOUD_SVG, { left: '68%' }));
      world.appendChild(this.actor('mario-scenery mario-scenery--hill', HILL_SVG, { left: '30%' }));
      world.appendChild(this.actor('mario-scenery mario-scenery--hill', HILL_SVG, { left: '82%' }));

      // A floating row of blocks. The FIRST brick (brickA) is positioned so it
      // ends up directly above the fixed runner when the scroll-in phase freezes
      // (world left 46% ≈ runner 30% - freeze offset 16%). Only brickA is broken.
      const blockRow = this.actor('mario-blocks', '', { left: '46%' });
      const brickA = this.block('mario-block mario-block--brick', BRICK_SVG);
      const question = this.block('mario-block mario-block--question', QUESTION_SVG);
      const brickB = this.block('mario-block mario-block--brick', BRICK_SVG);
      blockRow.append(brickA, question, brickB);
      world.appendChild(blockRow);

      // Warp pipes dot the level as scenery/obstacles.
      world.appendChild(this.actor('mario-pipe', PIPE_SVG, { left: '58%' }));
      world.appendChild(this.actor('mario-pipe mario-pipe--tall', PIPE_SVG, { left: '90%' }));

      // The stompable walker, positioned so the runner comes down on it during
      // the resumed scroll phase.
      const walker = this.actor('mario-walker', WALKER_SVG, { left: '72%' });
      world.appendChild(walker);

      // The level finish: a flagpole followed by a castle, set well to the right
      // of the last pipe (world 90%) and hill (world 82%) so there's clear
      // separation before the flag. The castle is placed so its central door
      // ends up over the fixed runner (30%) when the rest-scroll reaches its
      // frozen end offset (-97%): castle left 126% lands at ~29%, so the level
      // stops with Mario standing just in front of the castle door.
      world.appendChild(this.actor('mario-flag', FLAG_SVG, { left: '116%' }));
      world.appendChild(this.actor('mario-castle', CASTLE_SVG, { left: '126%' }));

      stage.appendChild(world);

      // The runner sits fixed at 30% of the stage the whole time; the world
      // scrolls beneath it. The body is static; two overlaid leg frames alternate
      // via a CSS steps() cycle so the run reads as a clean two-frame sprite.
      const runner = add(document.createElement('div'));
      runner.className = 'mario-runner';
      runner.innerHTML =
        `<div class="mario-runner__body">` +
        `<div class="mario-runner__layer mario-runner__legs mario-runner__legs--a">${HERO_LEGS_A_SVG}</div>` +
        `<div class="mario-runner__layer mario-runner__legs mario-runner__legs--b">${HERO_LEGS_B_SVG}</div>` +
        `<div class="mario-runner__layer mario-runner__torso">${HERO_BODY_SVG}</div>` +
        `</div>`;
      stage.appendChild(runner);

      const legs = Array.from(
        runner.querySelectorAll<HTMLElement>('.mario-runner__legs')
      );
      const pauseLegs = (paused: boolean): void => {
        for (const leg of legs) {
          leg.style.animationPlayState = paused ? 'paused' : 'running';
        }
      };
      const pauseParallax = (paused: boolean): void => {
        for (const s of world.querySelectorAll<HTMLElement>('.mario-scenery')) {
          s.style.animationPlayState = paused ? 'paused' : 'running';
        }
      };

      // --- phased choreography ------------------------------------------------

      // Phase 1: scroll in until the first brick sits directly over the runner.
      world.classList.add('mario-world--scroll-in');
      await waitForAnimationEnd(world, 'mario-world-in', signal);
      if (signal.aborted) {
        return;
      }

      // Phase 2: background is frozen (the scroll-in animation holds its end
      // state via fill:both). Stop the parallax + leg cycle so the whole scene
      // truly pauses, then Mario jumps straight up.
      pauseParallax(true);
      pauseLegs(true);
      runner.classList.add('mario-runner--jump');

      // Burst the first brick early — just after the background stops, near the
      // start of the upward arc (~25% of JUMP_MS) rather than at the apex.
      await delay(Math.round(MarioLevelAnimation.JUMP_MS * 0.25), signal);
      if (signal.aborted) {
        return;
      }
      brickA.classList.add('mario-block--bumped');
      this.burstBrick(brickA, world, add);

      // Wait for the landing to finish before resuming.
      await waitForAnimationEnd(runner, 'mario-jump', signal);
      if (signal.aborted) {
        return;
      }
      runner.classList.remove('mario-runner--jump');

      // Phase 3: resume the scroll (and the parallax + leg cycle) for the rest
      // of the level. The second brick is left intact.
      pauseParallax(false);
      pauseLegs(false);
      world.classList.remove('mario-world--scroll-in');
      world.classList.add('mario-world--scroll-rest');

      // The walker (world 72%) aligns with the runner (30%) when the world
      // reaches -42%, i.e. ~32% through the rest phase (from -16% to -97%).
      // Dip-stomp it flat as Mario passes over it — pulled ~30px earlier so the
      // dip clearly leads the contact.
      await delay(Math.round(MarioLevelAnimation.SCROLL_REST_MS * 0.28), signal);
      if (!signal.aborted) {
        runner.classList.add('mario-runner--stomp');
        walker.classList.add('mario-walker--stomped');
        walker.innerHTML = `<div class="mario-actor__body">${WALKER_FLAT_SVG}</div>`;
      }

      // End the pass when the resumed scroll finishes carrying the level up to
      // the castle: Mario is now standing just in front of the castle door.
      // Stop his run cycle so he stands still at the door, hold a beat, then
      // fade the whole stage out.
      await waitForAnimationEnd(world, 'mario-world-rest', signal);
      if (signal.aborted) {
        return;
      }
      runner.classList.add('mario-runner--still');
      await delay(600, signal);
      if (signal.aborted) {
        return;
      }
      stage.classList.add('mario-stage--finish');
      await waitForAnimationEnd(stage, 'mario-stage-fade', signal);
    } finally {
      cleanup();
    }
  }

  // Builds a positioned scenery/actor element wrapping an SVG silhouette (or an
  // empty container when svg is '').
  private actor(
    className: string,
    svg: string,
    style: Partial<CSSStyleDeclaration>
  ): HTMLElement {
    const el = document.createElement('div');
    el.className = className;
    if (svg) {
      el.innerHTML = `<div class="mario-actor__body">${svg}</div>`;
    }
    Object.assign(el.style, style);
    return el;
  }

  // Builds a single block for the floating block row.
  private block(className: string, svg: string): HTMLElement {
    const el = document.createElement('div');
    el.className = className;
    el.innerHTML = `<div class="mario-actor__body">${svg}</div>`;
    return el;
  }

  // Replaces a brick with a short burst of four shards flying outward, then
  // removes the whole thing when the burst finishes.
  private burstBrick(
    brick: HTMLElement,
    world: HTMLElement,
    add: (el: HTMLElement) => HTMLElement
  ): void {
    const rect = brick.getBoundingClientRect();
    const worldRect = world.getBoundingClientRect();
    const cx = rect.left - worldRect.left + rect.width / 2;
    const cy = rect.top - worldRect.top + rect.height / 2;

    brick.classList.add('mario-block--broken');

    const dirs = [
      { dx: -26, dy: -30 },
      { dx: 26, dy: -34 },
      { dx: -14, dy: -22 },
      { dx: 14, dy: -26 }
    ];
    for (const dir of dirs) {
      const shard = add(document.createElement('div'));
      shard.className = 'mario-shard';
      shard.style.left = `${cx - 5}px`;
      shard.style.top = `${cy - 5}px`;
      shard.style.setProperty('--mario-shard-dx', `${dir.dx}px`);
      shard.style.setProperty('--mario-shard-dy', `${dir.dy}px`);
      shard.innerHTML = SHARD_SVG;
      world.appendChild(shard);
    }
  }
}
